import { Check } from 'lucide-react';
import { type FC } from 'react';

/**
 * The round mark on Figma's `Rich Checkbox Group`: a hairline ring when clear,
 * the disc filled brand green behind a white tick when ticked.
 *
 * Round and still a checkbox wherever it is used. Every frame that draws it
 * puts it on a list that takes any number of its options, and radio semantics
 * would announce "1 of n" for a field that is not one of n. The shape is the
 * frame's; the semantics belong to the control this decorates, which is always
 * a real `<input type="checkbox">` the caller hides.
 *
 * Decorative on its own — `aria-hidden` — because the input beside it carries
 * the state and the label carries the name.
 *
 * The ring is stone-950 rather than a border token: the frame draws it at
 * #0C0A09, the ramp's darkest step, and `--ds-color-border-primary` is
 * #E5E5E5. Ticked fills the disc brand green behind a 14px tick, and keeps a
 * 1px border in the same green, as the frame does.
 *
 * ## Both states are 16x16, which the frame's are not
 *
 * Figma draws the clear state as a 14px ring inset 1px inside the 16px box and
 * the ticked state as a full 16px disc. Rendered, that grows the circle by 2px
 * on tick and shifts every row it sits in — the mark's slot is 14 wide in one
 * state and 16 in the other, so the label beside it moves too.
 *
 * One element rather than two, so the geometry cannot differ by construction:
 * the box is `size-4` with a 1px border in both states and only the fill, the
 * border colour and the glyph change. `border-box` is the app-wide default, so
 * the border is inside the 16 and the two states are concentric. That is a
 * deliberate 2px departure from the clear state's export.
 *
 * Shared because two fields draw it — the project Settings tab's type grid and
 * the create project form's type groups — and these values are what must not
 * drift between them.
 */
export const RichCheckboxMark: FC<{ checked: boolean }> = ({ checked }) => (
  <span
    aria-hidden="true"
    className={[
      'flex size-4 shrink-0 items-center justify-center rounded-full border border-solid',
      'transition-colors duration-[120ms] ease-[ease] motion-reduce:transition-none',
      checked
        ? 'border-[var(--ds-color-bg-brand)] bg-[var(--ds-color-bg-brand)]'
        : 'border-[var(--ds-color-raw-stone-950)] bg-[var(--ds-color-bg-primary)] shadow-[0_1px_2px_0_rgb(0_0_0/0.05)]',
    ].join(' ')}
  >
    {/* Always mounted, so the tick has something to animate from. It scales
        up out of the disc as the fill arrives, which is the whole of the
        motion on a tick — the box itself must not move, or every row it sits
        in moves with it. */}
    <Check
      className={`size-3.5 text-[var(--ds-color-text-invert)] transition-[opacity,transform] duration-[120ms] ease-[ease] motion-reduce:transition-none ${
        checked ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
      }`}
      strokeWidth={2}
    />
  </span>
);
